import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CoreService } from '@app/@core/services/core.service';
import {
  AuthenticationService,
  LoginResponse,
  RegisterAccountData,
} from '@app/auth/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.scss'],
  standalone: false,
})
export class VerifyOtpComponent implements OnInit {
  error: string | undefined;

  isLoading = false;
  verifyOtpForm: FormGroup;

  isTimerActive = false;
  timer: number;
  timerSubscription: Subscription;

  otpConfig = { length: 6, allowNumbersOnly: true };

  @Input() accountData: RegisterAccountData;

  @Output() back = new EventEmitter<void>();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthenticationService,
    private router: Router,
    private coreService: CoreService,
    private translateService: TranslateService,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.verifyOtpForm = this.formBuilder.group({
      code: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });
  }

  resendOtp(
    { phone, email }: { phone?: string; email?: string } = {},
    successCB = () => {},
  ) {
    this.isLoading = true;
    let phoneNumber = phone || this.accountData.phone;
    if (('' + phoneNumber).charAt(0) !== '+') {
      phoneNumber = `+${phoneNumber}`;
    }
    this.authService
      .sendOtp('', phoneNumber, email)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          successCB();
          const expiryTimeInMilliSeconds = response?.expiryTime - Date.now(); // Extract the expiry time from the response
          let expiryTimeInSeconds = 60;
          if (expiryTimeInMilliSeconds > 0) {
            expiryTimeInSeconds = Math.floor(expiryTimeInMilliSeconds / 1000);
          }
          this.startTimer(expiryTimeInSeconds);
        },
        (err) => {
          this.coreService.showErrorDialog(err.error?.message);
        },
      );
  }

  startTimer(duration: number) {
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer === 0) {
        this.stopTimer();
      }
    });
  }

  getTimerText(sec: number) {
    if (sec > 60) {
      const minutes = Math.floor(sec / 60);
      const seconds = sec % 60;
      return `${minutes} ${this.translateService.instant(
        'minute(s)',
      )} ${this.translateService.instant(
        'and',
      )} ${seconds} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.isTimerActive = false;
  }

  goBack() {
    this.back.emit();
  }

  verifyOTP() {
    if (this.verifyOtpForm.valid && this.accountData.phone) {
      const code = this.verifyOtpForm.value.code;
      const phone = this.accountData.phone;
      this.isLoading = true;
      this.authService
        .verifyOtp('', code, phone)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          (res) => {
            this.registerAccount(res.token);
          },
          (err) => {
            this.error = 'Incorrect Otp ';
            this.isLoading = false;
            this.coreService.showErrorDialog(err);
          },
        );
    }
  }

  private registerAccount(token: string) {
    delete this.accountData.terms;
    this.authService
      .register(this.accountData, token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.login();
        },
        (err) => {
          this.coreService.showErrorDialog(err?.error || err);
          this.isLoading = false;
        },
      );
  }

  private login() {
    this.authService
      .login({
        username: this.accountData.email,
        password: this.accountData.password,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.saveCredentials(res);
        },
        (err) => {},
      );
  }

  private saveCredentials(credentials: LoginResponse) {
    this.authService.setCredentials({
      username: this.accountData.email,
      token: credentials.access_token,
      companyId: '0',
      refreshToken: credentials.refresh_token,
      companyName: undefined,
    });
    window.location.href = '/dashboard';
  }
}
